import React, { useState } from 'react';
import store, { loanActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { LoanApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { LoadPanel } from 'devextreme-react/load-panel';
import { LoanType, LoanTypeOptions } from '../common/LoanConstants';
import { formatCurrency } from '../../common/SharedFunctions';
import { RefreshEvents } from '../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const ChangeLoanTypeModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.changeLoanTypeModal);
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [loan, set_loan] = useState(null);
    const [targetLoanTypeId, set_targetLoanTypeId] = useState(null);
    const [masterNoteId, set_masterNoteId] = useState(null);

    const [openConfirmModal, set_openConfirmModal] = useState(false);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const resetState = () => {
        set_loan(null);
        set_targetLoanTypeId(null);
        set_masterNoteId(null);
        set_openConfirmModal(false);
        set_loadingOpen(false);
    };

    React.useEffect(() => {
        if (!isOpen || !loanId) {
            resetState();
            return;
        }

        const loadLoanInfoAsync = async () => {
            await loadLoanInfo(loanId);
        };
        loadLoanInfoAsync();
    }, [loanId]);

    const loadLoanInfo = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await loanApi.getChangeLoanType(id);
            if (response.statusCode !== 200) {
                return;
            }
            const ln = response.result;
            set_loan(ln);
            if (ln.targetLoanTypes.length > 0) {
                set_targetLoanTypeId(ln.targetLoanTypes[0].id);
            }
            if (ln.masterNotes.length > 0) {
                set_masterNoteId(ln.masterNotes[0].id);
            }
        } finally {
            set_loadingOpen(false);
        }
    };

    const onConfirmModalCallback = async () => {
        await saveChanges();
        set_openConfirmModal(false);
    };

    const _onSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        set_openConfirmModal(true);
    };

    const saveChanges = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: loan.id,
                loanTypeId: targetLoanTypeId,
                masterNoteId,
            };

            const response = await loanApi.setChangeLoanType(loan.id, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Loan updated successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const _handleClose = () => {
        dispatch(
            loanActions.setChangeLoanTypeModal({
                changeLoanTypeModal: { isOpen: false },
            })
        );
    };

    const styles = {
        buttonMarginRight: {
            marginRight: '5px',
        },
        modal: {
            width: 600,
        },
        content: {
            height: 'auto',
            margin: '0',
            padding: '10px',
        },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        nonEditable: { backgroundColor: '#f7f7f7', padding: '5px 10px' },
        infodata: { fontWeight: 'bold' },
        warning: {
            padding: '10px',
            color: 'darkred',
            width: '100%',
            textAlign: 'center',
        },
    };

    if (!isOpen || !loanId || !loan) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='lg'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex={-1}
                        role='dialog'
                        style={styles.modal}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <form
                                name='changeTypeForm'
                                onSubmit={(event) => _onSubmit(event)}
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>Change Loan Type</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div
                                    className='main-content no-search'
                                    style={{ padding: '0' }}
                                >
                                    <div
                                        className='scrollable-content'
                                        style={styles.content}
                                    >
                                        <div style={styles.nonEditable}>
                                            <table>
                                                <tr>
                                                    <td
                                                        style={{
                                                            width: '150px',
                                                        }}
                                                    >
                                                        BFI Number:
                                                    </td>
                                                    <td style={styles.infodata}>{loan.bfiNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td>Borrower Name:</td>
                                                    <td style={styles.infodata}>{loan.borrowerContactName}</td>
                                                </tr>
                                                <tr>
                                                    <td>Entity Name:</td>
                                                    <td style={styles.infodata}>{loan.borrowerEntityName}</td>
                                                </tr>
                                                <tr>
                                                    <td>Loan Type:</td>
                                                    <td style={styles.infodata}>
                                                        {LoanTypeOptions.find((a) => a.id === loan.loanTypeId).name}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Commitment:</td>
                                                    <td style={styles.infodata}>{formatCurrency(loan.commitment ?? 0, 0)}</td>
                                                </tr>
                                            </table>
                                        </div>
                                        <div>
                                            {loan.targetLoanTypes.length === 0 && (
                                                <div style={styles.warning}>
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    &nbsp;Sorry, but this loan does not support Loan Type changes!
                                                    <br />
                                                    {loan.error && (
                                                        <>
                                                            <br />
                                                            <div>{loan.error}</div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {loan.targetLoanTypes.length > 0 && (
                                                <>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={{
                                                                marginTop: '10px',
                                                            }}
                                                        >
                                                            <label htmlFor='targetLoanTypeId'>New Loan Type</label>
                                                            <select
                                                                id='targetLoanTypeId'
                                                                value={targetLoanTypeId}
                                                                onChange={(event) =>
                                                                    set_targetLoanTypeId(parseInt(event.target.value))
                                                                }
                                                            >
                                                                {loan.targetLoanTypes.map((t) => {
                                                                    return (
                                                                        <option
                                                                            key={t.id}
                                                                            value={t.id}
                                                                        >
                                                                            {t.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    {(targetLoanTypeId === LoanType.SubNoteConstruction ||
                                                        targetLoanTypeId === LoanType.SubNoteFixFlip) && (
                                                        <div>
                                                            <div>
                                                                Below is a list of Master Notes belonging to the Loan Borrower.
                                                            </div>
                                                            <label htmlFor='masterNoteId'>
                                                                Please select a Master Note to associate with...
                                                            </label>
                                                            <select
                                                                id='masterNoteId'
                                                                value={masterNoteId}
                                                                onChange={(event) =>
                                                                    set_masterNoteId(parseInt(event.target.value))
                                                                }
                                                            >
                                                                {loan.masterNotes.map((t) => {
                                                                    return (
                                                                        <option
                                                                            key={t.id}
                                                                            value={t.id}
                                                                        >
                                                                            {t.bfiNumber && t.bfiNumber + ' - '} Line{' '}
                                                                            {formatCurrency(t.commitment, 0)} - {t.status}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                                disabled={loan.targetLoanTypes.length === 0}
                                            >
                                                Submit
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Dialog>
            <ConfirmModal
                isOpen={openConfirmModal}
                text='Are you sure you want to change the loan type?'
                confirmCallback={onConfirmModalCallback}
                cancelCallback={() => set_openConfirmModal(false)}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default ChangeLoanTypeModal;
